/**
 * Popover state for the studio's Template control: open/closed, the deck-screen search, the
 * filtered groups the list renders, and where the popover sits on screen.
 *
 * The popover is rendered through a portal, so its position is computed here rather than left to
 * CSS: `.card` in StudioHeader sets `overflow: hidden` for its rounded corners, which clipped an
 * absolutely-positioned panel at the Report Details boundary and hid everything below the first few
 * templates. Position is refreshed on scroll and resize because a fixed element does not move with
 * the page the way an absolute one does.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CatalogGroup } from 'src/components/star-grid/api/reportEngineTypes';
import { useStudioTemplateSources, type StudioTemplateSources } from 'src/components/star-grid/hooks/useStudioTemplateSources';

const PANEL_GAP = 4;
const VIEWPORT_MARGIN = 12;

export interface PopoverPosition {
  readonly top: number;
  readonly left: number;
  readonly width: number;
}

export interface StudioTemplatePickerController {
  readonly sources: StudioTemplateSources;
  readonly open: boolean;
  readonly toggle: () => void;
  readonly query: string;
  readonly setQuery: (value: string) => void;
  /** Deck screens matching the query, with emptied groups dropped. */
  readonly visibleProphecy: readonly CatalogGroup[];
  readonly triggerRef: React.RefObject<HTMLButtonElement>;
  readonly popoverRef: React.RefObject<HTMLDivElement>;
  readonly position: PopoverPosition | undefined;
  /** Runs a selection and closes the popover — a choice is always the last act in the popover. */
  readonly choose: (run: () => void) => () => void;
}

const matches = (haystack: readonly (string | null | undefined)[], needle: string): boolean =>
  !needle || haystack.some((value) => (value ?? '').toLowerCase().includes(needle.toLowerCase()));

export function useStudioTemplatePicker(): StudioTemplatePickerController {
  const sources = useStudioTemplateSources();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<PopoverPosition | undefined>(undefined);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.min(rect.width, window.innerWidth - VIEWPORT_MARGIN * 2);
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.right - width, window.innerWidth - width - VIEWPORT_MARGIN));
    setPosition({ top: rect.bottom + PANEL_GAP, left, width });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    // A click outside or Escape closes it. The popover is portalled, so it is NOT inside the
    // trigger's container — both have to be checked or every click in the panel would close it.
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !popoverRef.current?.contains(target)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  const visibleProphecy = useMemo(
    () =>
      sources.prophecy
        .map((group) => ({
          ...group,
          screens: group.screens.filter((screen) => matches([screen.name, group.group, screen.row_dimension], query))
        }))
        .filter((group) => group.screens.length > 0),
    [sources.prophecy, query]
  );

  const choose = useCallback(
    (run: () => void) => () => {
      run();
      setOpen(false);
    },
    []
  );

  return {
    sources,
    open,
    toggle: useCallback(() => setOpen((was) => !was), []),
    query,
    setQuery,
    visibleProphecy,
    triggerRef,
    popoverRef,
    position,
    choose
  };
}
